import { useCallback, useEffect, useRef, useState } from 'react';

import type { AssetLoadCallback, Event, Layout, RiveFile } from '@rive-app/react-canvas';
import { EventType, useRive, useStateMachineInput } from '@rive-app/react-canvas';
import { reaction } from 'mobx';

import { logToNewRelic } from '../../services/debug';
import { useStores } from '../../stores/useStores';
import { LOADING_TIMED_OUT_MESSAGE, useLoadingTimeout } from '../useLoadingTimeout';

import { RIVE_DEFAULTS } from './constants';
import './initRiveRuntime';
import { logRiveLoad, riveLoadInstrumentation } from './riveLoadInstrumentation';

interface UseRiveMachineProps {
  src?: string;
  stateMachineName?: string;
  autoplay?: boolean;
  autoBind?: boolean;
  layout?: Layout;
  artboard?: string;
  onLoad?: () => void;
  onLoadError?: (error: any) => void;
  loadingTimeout?: number;
  onStateChange?: (event: Event) => void;
  onRiveEvent?: (event: Event) => void;
  assetLoader?: AssetLoadCallback;
}

export const useRiveMachine = ({
  src,
  stateMachineName = RIVE_DEFAULTS.STATE_MACHINE_NAME,
  autoplay = true,
  autoBind = false,
  artboard,
  onLoad,
  onLoadError,
  loadingTimeout = RIVE_DEFAULTS.TIMEOUT,
  layout = RIVE_DEFAULTS.LAYOUT,
  onStateChange,
  onRiveEvent,
  assetLoader,
}: UseRiveMachineProps) => {
  const { riveFileStore } = useStores();
  const [error, setError] = useState<Event | null>(null);
  const [hasRiveLoaded, setHasRiveLoaded] = useState<boolean>(false);
  const [riveFile, setRiveFile] = useState<RiveFile | undefined>(() => (src ? riveFileStore.getFile(src) : undefined));

  const startedAtRef = useRef<number>(0);
  const mountInfoRef = useRef<{ active: number; mountIndex: number } | null>(null);
  const loggedRef = useRef<boolean>(false);
  const cacheHitAtMountRef = useRef<boolean>(false);

  // Subscribe to the MobX cache so consumers that aren't wrapped in
  // observer() still re-render when the preloader fills the file in.
  // Also ref-counts the entry so the store can release the underlying
  // RiveFile (and free WASM memory) once the last consumer unmounts.
  useEffect(() => {
    if (!src) {
      setRiveFile(undefined);
      return;
    }

    const initial = riveFileStore.acquire(src);
    setRiveFile(initial);

    startedAtRef.current = performance.now();
    loggedRef.current = false;
    cacheHitAtMountRef.current = Boolean(initial);
    mountInfoRef.current = riveLoadInstrumentation.incActive();

    const dispose = reaction(
      () => riveFileStore.getFile(src),
      (file) => setRiveFile(file),
    );

    return () => {
      dispose();
      riveFileStore.release(src);
      riveLoadInstrumentation.decActive();
      // Emit an abandoned-load row if the canvas never finished loading.
      if (!loggedRef.current && startedAtRef.current > 0) {
        loggedRef.current = true;
        logRiveLoad({
          src,
          durationMs: Math.round(performance.now() - startedAtRef.current),
          activeRiveCount: mountInfoRef.current?.active ?? 0,
          mountIndex: mountInfoRef.current?.mountIndex ?? 0,
          cacheHit: cacheHitAtMountRef.current,
          isPreload: false,
          outcome: 'abandoned',
          timeoutMs: loadingTimeout,
        });
      }
    };
  }, [riveFileStore, src, loadingTimeout]);

  const handleLoad = useCallback(() => {
    setHasRiveLoaded(true);
    onLoad?.();
    if (loggedRef.current) return;
    loggedRef.current = true;
    logRiveLoad({
      src,
      durationMs: Math.round(performance.now() - startedAtRef.current),
      activeRiveCount: mountInfoRef.current?.active ?? riveLoadInstrumentation.getActive(),
      mountIndex: mountInfoRef.current?.mountIndex ?? 0,
      cacheHit: cacheHitAtMountRef.current,
      isPreload: false,
      outcome: 'success',
      timeoutMs: loadingTimeout,
    });
  }, [onLoad, src, loadingTimeout]);

  const handleLoadError = useCallback(
    (loadError: any) => {
      onLoadError?.(loadError);
      setError(loadError);
      logToNewRelic('Rive Load Error', { message: loadError, src }, 'error');
      if (loggedRef.current) return;
      loggedRef.current = true;
      const isTimeout = loadError === LOADING_TIMED_OUT_MESSAGE;
      logRiveLoad({
        src,
        durationMs: Math.round(performance.now() - startedAtRef.current),
        activeRiveCount: mountInfoRef.current?.active ?? riveLoadInstrumentation.getActive(),
        mountIndex: mountInfoRef.current?.mountIndex ?? 0,
        cacheHit: cacheHitAtMountRef.current,
        isPreload: false,
        outcome: isTimeout ? 'timeout' : 'error',
        errorMessage: typeof loadError === 'string' ? loadError : String(loadError),
        timeoutMs: loadingTimeout,
      });
    },
    [onLoadError, src, loadingTimeout],
  );

  const { rive, RiveComponent } = useRive({
    src,
    riveFile,
    stateMachines: stateMachineName,
    artboard,
    autoplay,
    autoBind,
    layout,
    assetLoader,
    onLoad: handleLoad,
    onLoadError: handleLoadError,
    onStateChange,
  });

  useEffect(() => {
    if (!rive || !onRiveEvent) return;

    rive.on(EventType.RiveEvent, onRiveEvent);

    return () => {
      rive.off(EventType.RiveEvent, onRiveEvent);
    };
  }, [rive, onRiveEvent]);

  useEffect(() => {
    setHasRiveLoaded(false);
    setError(null);
  }, [src]);

  const isLoaded = hasRiveLoaded || Boolean(riveFile) || Boolean(rive?.isPlaying);

  const { isLoading: isTimeoutLoading, error: timeoutError } = useLoadingTimeout({
    resource: src,
    hasLoaded: isLoaded,
    timeoutEnabled: Boolean(src) && !riveFile,
    timeout: loadingTimeout,
    onTimeout: handleLoadError,
  });

  const fireRiveViewModelTrigger = useCallback(
    (triggerPath: string): boolean => {
      if (!rive) return false;

      const vm = rive.viewModelInstance;

      if (!vm) return false;

      const trigger = vm.trigger?.(triggerPath);

      if (!trigger) return false;

      if (typeof trigger.trigger === 'function') {
        trigger.trigger();
        return true;
      } else {
        logToNewRelic('Rive View Model Trigger', { message: 'Trigger not found', triggerPath }, 'error');
      }

      return false;
    },
    [rive],
  );

  const setRiveViewModelEnum = useCallback(
    (enumPath: string, value: string): boolean => {
      if (!rive) return false;

      const vm = rive.viewModelInstance;
      if (!vm) return false;

      const enumProp = vm.enum?.(enumPath);
      if (!enumProp) return false;

      enumProp.value = value;
      return true;
    },
    [rive],
  );

  const setRiveViewModelBoolean = useCallback(
    (booleanPath: string, value: boolean): boolean => {
      if (!rive) return false;

      const vm = rive.viewModelInstance;
      if (!vm) return false;

      const booleanProp = vm.boolean?.(booleanPath);
      if (!booleanProp) return false;

      booleanProp.value = value;
      return true;
    },
    [rive],
  );

  return {
    rive,
    isLoading: isTimeoutLoading && !riveFile,
    timeoutError,
    error,
    RiveComponent,
    useStateMachineInput,
    fireRiveViewModelTrigger,
    setRiveViewModelEnum,
    setRiveViewModelBoolean,
  };
};
